"use client";

/**
 * AiLabelInlineEditor — edit a track/release's AI "music ingredients" label
 * directly on the track page, WYSIWYG: the real MusicIngredientsLabel renders
 * live above the form as you type. Persists `aiDisclosure` to the work|release
 * doc. Only shown to admins/creators (owner-or-admin, matching Firestore rules).
 */

import { useState } from "react";
import { doc, setDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { AIDisclosureEditor } from "./ai-disclosure-editor";
import { MusicIngredientsLabel } from "@/components/music/music-ingredients-label";
import type { AIDisclosure } from "@/data/ai-disclosure";
import { Button } from "@/components/ui/button";
import { Wand2, Check, X, Loader2 } from "lucide-react";

export function AiLabelInlineEditor({
  mediaId,
  collectionName,
  canEdit,
  initial,
  trackTitle,
  onSaved,
}: {
  mediaId: string;
  collectionName: "works" | "releases";
  canEdit: boolean;
  initial?: AIDisclosure | null;
  trackTitle?: string;
  onSaved?: (d: AIDisclosure) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<AIDisclosure>(initial && initial.level ? initial : { level: "human" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!canEdit) return null;

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      await setDoc(doc(db, collectionName, mediaId), { aiDisclosure: draft }, { merge: true });
      onSaved?.(draft);
      setEditing(false);
    } catch (e: any) {
      setErr(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setDraft(initial && initial.level ? initial : { level: "human" });
          setErr(null);
          setEditing(true);
        }}
        className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/25"
      >
        <Wand2 className="h-3.5 w-3.5" /> {initial?.level ? "Edit AI / label info" : "Add AI / label info"}
      </button>
    );
  }

  return (
    <div className="mt-3 rounded-xl border border-border bg-muted/20 p-3 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          AI / Ingredients label — live preview
        </span>
        <div className="flex gap-1.5">
          <Button size="sm" variant="ghost" className="h-8 gap-1 text-xs" onClick={() => setEditing(false)}>
            <X className="h-3.5 w-3.5" /> Cancel
          </Button>
          <Button size="sm" className="h-8 gap-1 text-xs" onClick={save} disabled={saving}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save
          </Button>
        </div>
      </div>

      {/* Live WYSIWYG preview — the exact label listeners will see. */}
      {draft.level && (
        <div className="rounded-lg bg-background/50 p-2">
          <MusicIngredientsLabel disclosure={draft} trackTitle={trackTitle} />
        </div>
      )}

      {/* The form */}
      <AIDisclosureEditor value={draft} onChange={setDraft} />

      {err && <p className="text-xs text-red-500">{err}</p>}
    </div>
  );
}
